import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Loader2, X, Car } from 'lucide-react';
import { ApiService } from '../services/api';
import { Analytics } from '../services/analytics';
import { UserPreferences } from '../services/userPreferences';
import { useBranding } from '../context/BrandingContext';
import { BusinessModelHelpers } from '../utils/businessModel';
import { BusinessModel } from '../types';

type OnboardingStats = {
  vehicles: number;
  drivers: number;
  hires: number;
  verifiedAgreements: number;
  tickets: number;
  openTasks: number;
};

type StepKey = 'add_vehicle' | 'add_driver' | 'create_hire' | 'verify_agreement' | 'upload_ticket';

const ALL_STEPS: Array<{
  key: StepKey;
  title: string;
  description: string;
  href: string;
  requiredForModels: BusinessModel[];
}> = [
  {
    key: 'add_vehicle',
    title: 'Add your first vehicle',
    description: 'Create vehicles and tag your fleet.',
    href: '/vehicles?new=1',
    requiredForModels: ['hire_firm_registered', 'hire_firm_startup', 'fleet_operator']
  },
  {
    key: 'add_driver',
    title: 'Add your first hirer or driver',
    description: 'Capture the person FleetMode will match against PCN records.',
    href: '/drivers?new=1',
    requiredForModels: ['hire_firm_registered', 'hire_firm_startup']
  },
  {
    key: 'create_hire',
    title: 'Create a hire record',
    description: 'Link the hirer to the vehicle and hire period.',
    href: '/hires?new=1',
    requiredForModels: ['hire_firm_registered', 'hire_firm_startup']
  },
  {
    key: 'verify_agreement',
    title: 'Verify a hire agreement',
    description: 'Upload an agreement and confirm PCN liability wording is present.',
    href: '/hires?new=1',
    requiredForModels: ['hire_firm_registered', 'hire_firm_startup']
  },
  {
    key: 'upload_ticket',
    title: 'Upload your first PCN',
    description: 'Start the existing evidence and deadline workflow.',
    href: '/tickets?upload=1',
    requiredForModels: ['hire_firm_registered', 'hire_firm_startup', 'fleet_operator']
  }
];

export default function OnboardingChecklist({ forceOpen = false }: { forceOpen?: boolean }) {
  const navigate = useNavigate();
  const { organization } = useBranding();

  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [stats, setStats] = useState<OnboardingStats | null>(null);

  // Filter steps based on business model
  const STEPS = useMemo(() => {
    const businessModel = organization?.business_model || 'hire_firm_registered';
    return ALL_STEPS.filter(step =>
      step.requiredForModels.includes(businessModel)
    );
  }, [organization]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const isDismissed = await UserPreferences.get<boolean>('onboarding.dismissed', false);
      if (mounted) setDismissed(Boolean(isDismissed));
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await ApiService.getOnboardingStats?.();
        if (mounted) setStats(data || null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const stepStatus = useMemo(() => {
    const s = stats;
    return {
      add_vehicle: (s?.vehicles || 0) > 0,
      add_driver: (s?.drivers || 0) > 0,
      create_hire: (s?.hires || 0) > 0,
      verify_agreement: (s?.verifiedAgreements || 0) > 0,
      upload_ticket: (s?.tickets || 0) > 0
    } as Record<StepKey, boolean>;
  }, [stats]);

  const completedCount = useMemo(
    () => STEPS.reduce((acc, step) => acc + (stepStatus[step.key] ? 1 : 0), 0),
    [STEPS, stepStatus]
  );
  const allDone = completedCount === STEPS.length;

  const shouldHide = (!forceOpen && dismissed) || (!forceOpen && allDone);
  if (shouldHide) return null;

  const handleDismiss = async () => {
    setDismissed(true);
    await UserPreferences.set('onboarding.dismissed', true);
    Analytics.track('onboarding_dismissed');
  };

  const handleGo = (href: string, step: StepKey) => {
    Analytics.track('onboarding_step_clicked', { step });

    // Track email campaign attribution
    Analytics.track('onboarding_step_action', {
      step,
      source: 'onboarding_checklist',
      completed_count: completedCount,
      total_steps: STEPS.length
    });

    navigate(href);
  };

  return (
    <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Getting started</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Complete these steps to set up your workspace ({completedCount}/{STEPS.length}).
          </p>
        </div>
        {!forceOpen && (
          <button
            onClick={handleDismiss}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Dismiss onboarding checklist"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Owned fleet guidance */}
      {BusinessModelHelpers.isFleetOperator(organization) && (
        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Car className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900 dark:text-blue-100">
              <strong>Owned fleet mode:</strong> You'll manage tickets through pay, appeal,
              or internal assignment workflows. Hire modules stay optional.
            </div>
          </div>
        </div>
      )}

      {BusinessModelHelpers.canTransferLiability(organization) && (
        <div className="mt-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-primary-900 dark:text-primary-100">
              <strong>PCO hire workflow:</strong> Set up vehicle, hirer, hire period, and agreement evidence.
              Transfer pack readiness is checked later per PCN by the existing liability workflow.
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading setup steps…
        </div>
      )}

      {!loading && (
        <div className="mt-5 space-y-3">
          {STEPS.map((step) => {
            const done = stepStatus[step.key];
            return (
              <div
                key={step.key}
                className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors ${
                  done
                    ? 'bg-green-50/60 dark:bg-green-900/10 border-green-200 dark:border-green-900/30'
                    : 'bg-slate-50/60 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={18} className={done ? 'text-green-600 dark:text-green-400' : 'text-slate-300 dark:text-slate-700'} />
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{step.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{step.description}</p>
                </div>
                {!done ? (
                  <button
                    onClick={() => handleGo(step.href, step.key)}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold transition-colors"
                  >
                    Go <ChevronRight size={14} />
                  </button>
                ) : (
                  <span className="shrink-0 text-xs font-bold text-green-700 dark:text-green-400">Done</span>
                )}
              </div>
            );
          })}

          <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-1">
            Tip: You can always open Help in the sidebar for quick links.
          </div>
        </div>
      )}
    </div>
  );
}

